import _get from 'lodash/get';
import _invoke from 'lodash/invoke';

import Analytics from '../analytics';
import {
    PLAYBACK_PAGE,
    HOME_PAGE,
    LANDING_PAGE,
    PLAYBACK_HEADER,
    REELS_PAGE,
    SEARCH_PAGE,
    VIDEO_SECTION,
    APP_RUNTIME_ERROR,
} from './constants';
import {
    getOrientation,
    capitalize,
} from './utils';

/**
 *
 * @desc Tracking utility
 *
 */

class Tracking {

    static trackPageView(pageName = VIDEO_SECTION) {

        const adobeAnalytics = _get(window, 'adobeAnalytics');
        const pageBase = _get(adobeAnalytics, 'pageName', 'Major League Baseball: Video: ').match(/.*: /)[0];
        const reportingSuiteId = _get(adobeAnalytics, 'account', 'mlbglobal08,mlbcom08');
        const fullPageName = `${pageBase}${pageName}`;
        const startupDeviceTypeString = capitalize(_invoke(window, 'getStartupDeviceTypeString') || 'desktop');
        const orientation = getOrientation();
        const deviceOrientation = `${startupDeviceTypeString}: ${orientation}`;
        const oktaId = _invoke(adobeAnalytics, 'Util.cookieRead', 'oktaid');
        const mlbOktaAuthClient = _invoke(window, 'mlbOkta.getAuthClient');
        const accessToken = _invoke(mlbOktaAuthClient, 'tokenManager.get', 'access_token');
        const loginStatus = _get(accessToken, 'accessToken') ? 'Logged In' : 'Not Logged In';
        const lang = _invoke(document, 'documentElement.getAttribute', 'lang') || 'en';
        const trackingData = {
            pageName: fullPageName,
            prop13: fullPageName,
            prop5: startupDeviceTypeString,
            prop6: orientation,
            prop7: deviceOrientation,
            prop9: lang === 'es' ? 'Spanish' : undefined,
            prop22: oktaId ? oktaId : null,
            eVar34: startupDeviceTypeString,
            eVar35: orientation,
            eVar36: deviceOrientation,
            prop39: loginStatus,
            eVar39: loginStatus,
            prop71: lang,
            eVar73: reportingSuiteId,
        };

        Analytics.trackPageView(trackingData);

    }

    static trackSearchResultsPageView() {

        this.trackPageView(SEARCH_PAGE);

    }

    static trackHomePageView = () => {

        this.trackPageView(HOME_PAGE);

    }

    static trackReelsPageView = () => {

        this.trackPageView(REELS_PAGE);

    }

    static trackLandingPageView = () => {

        this.trackPageView(LANDING_PAGE);

    }

    static trackPlaybackPageView = () => {

        this.trackPageView(PLAYBACK_PAGE);

    }

    static trackPlaybackPageSeeMoreButtonClick() {

        const baseText = `${PLAYBACK_PAGE} Module`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: See More: Click`,
        };

        Analytics.track(trackingData);

    }

    static trackSearchPageOnItemClick(e, itemIndex) {

        const baseText = `${PLAYBACK_PAGE} Module Search Results`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: Thumbnail ${itemIndex} Click`,
            eVar72: e.currentTarget.href,
        };

        Analytics.track(trackingData);

    }

    static trackSearchResultsPageOnSeeMoreClick() {

        const baseText = `${PLAYBACK_PAGE} Module Search Results`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText} See More: Click`,
        };

        Analytics.track(trackingData);

    }

    static trackSearchButtonClick = () => {

        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText}`,
            prop14: `${baseText}: Mobile Search Button: Click`,
        });

    }

    static trackSearchInputClick = () => {

        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText}`,
            prop14: `${baseText}: Search: Click`,
        });

    }

    static trackQuickSearchSelect = (filterName, filterValue) => {

        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText} Filters`,
            prop14: `${baseText}: Quick Search: ${filterName}: ${filterValue}`,
        });

    }

    static trackMenuButtonClick = () => {

        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText}`,
            prop14: `${baseText}: Explore: Click`,
        });

    }

    static trackMenuItemClick = (event, index) => {

        const baseText = PLAYBACK_HEADER;
        Analytics.track({
            prop12: `${baseText}`,
            prop14: `${baseText}: More Channels: Slot #${index}: ${event.currentTarget.innerText}`,
            eVar72: event.currentTarget.href,
        });

    }

    static trackLandingPageOnItemClick = (e, itemIndex) => {

        const baseText = `${PLAYBACK_PAGE} Module Landing Page`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: Thumbnail ${itemIndex} Click`,
            eVar72: e.currentTarget.href,
        };

        Analytics.track(trackingData);

    }

    static trackHomePageOnItemClick = (e, itemIndex) => {

        const trackingData = {
            prop12: HOME_PAGE,
            prop14: `${HOME_PAGE}: Thumbnail ${itemIndex} Click`,
            eVar72: e.currentTarget.href,
        };

        Analytics.track(trackingData);

    }

    static onShare = ({
        type,
        contentType,
        slug,
        title,
    }) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `Video Playback Page Module: Share Click: ${type}`,
            prop25: slug,
            eVar28: slug,
        });

        if(type === 'Facebook' || type === 'Twitter' || type === 'Reddit' || type === 'Pinterest') {

            const trackingData = {
                event: 'social-share',
                socialNetwork: type,
                contentType,
                contentId: slug,
                contentTitle: title,
            };

            Analytics.trackGoogleAnalytics(trackingData);

        }

    }

    static onMetadataToggle = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Expand Description`,
        });

    }

    static onPlaylistItemClick = (index, playlist, url) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Featured Playlist`,
            prop14: `${PLAYBACK_PAGE} Module: Featured Playlist ${playlist}: Thumbnail ${index} Click`,
            eVar72: url,
        });

    }

    static onSearchTagClick = (query, url) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Search: Click`,
            prop14: `${PLAYBACK_PAGE} Module: Search: Click "${query}"`,
            eVar72: url,
        });

    }

    static trackClearAllButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: Reset`,
        });

    }

    static trackFiltersMobileModalOpened = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: Open`,
        });

    }

    static trackFiltersMobileModalCloseButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: Close`,
        });

    }

    static trackMobileModalApplyButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: Apply Filters`,
        });

    }

    static trackFilterTabClick = (tabName, tabIndex) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: ${tabName}: ${tabIndex}`,
        });

    }

    static trackFilterAccordionHeaderExpanded = (accordionHeaderName, accordionHeaderIndex) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: ${accordionHeaderName}: ${accordionHeaderIndex}`,
        });

    }

    static trackFilterAccordionItemSelect = (accordionHeaderName, accordionItemName) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: ${accordionHeaderName}: ${accordionItemName}: Select`,
        });

    }

    static trackFilterAccordionItemDeselect = (accordionHeaderName, accordionItemName) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: ${accordionHeaderName}: ${accordionItemName}: Deselect`,
        });

    }

    static trackLandingPageSeeMoreButtonClick() {

        const baseText = `${PLAYBACK_PAGE} Module Landing Page`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: See More: Click`,
        };

        Analytics.track(trackingData);

    }

    static trackHomePageSeeMoreButtonClick() {

        const trackingData = {
            prop12: HOME_PAGE,
            prop14: `${HOME_PAGE}: See More: Click`,
        };

        Analytics.track(trackingData);

    }

    static trackFilterTagRemoval = () => {

        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText} Filters`,
            prop14: `${baseText}: Filters: Remove Tag`,
        });

    }

    static trackLightboxPreviousNextClick = (direction) => {

        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText} Lightbox`,
            prop14: `${baseText}: Lightbox: ${direction}`,
        });

    }

    static trackLightboxOpenClose = (open) => {

        const stateText = open ? 'Open': 'Close';
        const baseText = `${PLAYBACK_PAGE} Module`;
        Analytics.track({
            prop12: `${baseText} Lightbox`,
            prop14: `${baseText}: Lightbox: ${stateText}`,
        });

    }

    static trackShowLeaveBehind = (prevSlug, nextSlug) => {

        Analytics.track({
            prop12: 'Video: Leave-Behind',
            prop14: 'Video: Leave-Behind: Impression',
            prop16: `leave-behind:${nextSlug}`,
            prop25: prevSlug,
            eVar28: prevSlug,
        });

    }

    static trackLeaveBehindNextClipClick = (prevSlug, nextSlug) => {

        Analytics.track({
            prop12: 'Video: Leave-Behind',
            prop14: 'Video: Leave-Behind: Play Next Click',
            prop16: `leave-behind:${nextSlug}`,
            prop25: prevSlug,
            eVar28: prevSlug,
        });

    }

    static trackLeaveBehindReplay = (prevSlug, nextSlug) => {

        Analytics.track({
            prop12: 'Video: Leave-Behind',
            prop14: 'Video: Leave-Behind: Replay Click',
            prop16: `leave-behind:${nextSlug}`,
            prop25: prevSlug,
            eVar28: prevSlug,
        });

    }

    static trackLeaveBehindCountdownComplete = (prevSlug, nextSlug) => {

        Analytics.track({
            prop12: 'Video: Leave-Behind',
            prop14: `Video: Leave-Behind: Time Out`,
            prop16: `leave-behind:${nextSlug}`,
            prop25: prevSlug,
            eVar28: prevSlug,
        });

    }

    static trackRecsForPlaylist = ({
        currentSlug, modelType, modelVersion, topicId, puid,
    }) => {

        const upNextText = 'Video: Up Next Playlist';
        Analytics.track({
            prop12: upNextText,
            prop14: `${upNextText}: Impression`,
            prop25: currentSlug,
            eVar28: currentSlug,
            eVar93: modelVersion,
            eVar94: modelType,
            eVar95: topicId,
            eVar96: puid,
        });

    }

    static trackClipMenuClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Clip Menu`,
            prop14: `${PLAYBACK_PAGE} Module: Clip Menu Click: Clip Menu Click`,
        });

    }

    static trackClipMenuShareButtonClick = (slug) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Clip Menu`,
            prop14: `${PLAYBACK_PAGE} Module: Clip Menu Click: Share Click`,
            prop25: slug,
            eVar28: slug,
        });

    }

    static trackClipMenuAddToReelButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Clip Menu`,
            prop14: `${PLAYBACK_PAGE} Module: Clip Menu Click: Add to Reel Click`,
        });

    }

    static trackClipMenuRemoveFromReelButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Clip Menu`,
            prop14: `${PLAYBACK_PAGE} Module: Clip Menu Click: Remove from Reel Click`,
        });

    }

    static trackClipMenuDeleteReelButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Clip Menu`,
            prop14: `${PLAYBACK_PAGE} Module: Clip Menu Click: Delete Reel Click`,
        });

    }

    static trackReelCreationCreateReelClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Create Reel Click`,
        });

    }

    static trackReelCreationModalClose = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Close`,
        });

    }

    static trackMoveReelDownClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Reorder: Down`,
        });

    }

    static trackMoveReelUpClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Reorder: Up`,
        });

    }

    static trackRemoveReelClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Remove Clip`,
        });

    }

    static trackDragReelClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Reorder: Drag Click`,
        });

    }

    static trackReelCreationShareClick = (slug) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Share Click`,
            prop25: slug,
            eVar28: slug,
        });

    }

    static trackReelViewYourReelsClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: View Your Reels Click`,
        });

    }

    static trackAddToReelButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Add Clip to Reel: Click`,
        });

    }

    static trackRemoveFromReelButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Remove Clip from Reel: Click`,
        });

    }

    static trackReelCreationFormThumbnailClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Thumbnail Click`,
        });

    }

    static trackReelCreationFormTitleClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Title Click`,
        });

    }

    static trackReelCreationFormSubmit = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Title and Thumbnail Confirmation`,
        });

    }

    static trackReelCreationOpenClipTrimmingModal = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Clip Trimming: Modal Open`,
        });

    }


    static trackReelCreationSettingInOutPoints = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Clip Trimming: In Point-Out Point: Select`,
        });

    }

    static trackReelCreationEventButtonClick = (event) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Clip Trimming: ${event}: Click`,
        });

    }

    static trackReelCreationSaveClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Clip Trimming: Save: Click`,
        });

    }

    static trackReelCreationCloseClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Reel Creation`,
            prop14: `${PLAYBACK_PAGE} Module Reel Creation: Clip Trimming: Close: Click`,
        });

    }

    static trackFanCreatedLinkClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: View All User Reels: Click`,
        });

    }

    static trackFilmRoomLogoClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Home: Click`,
        });

    }

    static trackReelsPageOnItemClick = (e, itemIndex) => {

        const baseText = `${REELS_PAGE} Module`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: Thumbnail ${itemIndex} Click`,
            eVar72: e.currentTarget.href,
        };

        Analytics.track(trackingData);

    }

    static trackReelsPageSeeMoreButtonClick() {

        const baseText = `${REELS_PAGE} Module`;
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: See More: Click`,
        };

        Analytics.track(trackingData);

    }

    static trackSearchEvents(searchTerm, searchResults) {

        const trackingData = {
            event: 'search-selection',
            searchType: 'Video',
            searchTerm,
            searchResults,
        };

        Analytics.trackGoogleAnalytics(trackingData);

    }

    static trackReelCreated() {

        const trackingData = {
            'event': 'reel-created',
        };

        Analytics.trackGoogleAnalytics(trackingData);

    }

    static trackUserEducationImpression() {

        const baseText = 'Video: User Education Intercept';
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: Impression`,
        };

        Analytics.track(trackingData);

    }

    static trackUserEducationCloseButtonClick() {

        const baseText = 'Video: User Education Intercept';
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: Close Click`,
        };

        Analytics.track(trackingData);

    }

    static trackUserEducationGetStartedClick() {

        const baseText = 'Video: User Education Intercept';
        const trackingData = {
            prop12: baseText,
            prop14: `${baseText}: Button Click`,
        };

        Analytics.track(trackingData);

    }

    static trackVideoStart({
        title,
        slug,
        duration,
    }) {

        const trackingData = {
            event: 'video-engagement',
            videoName: title,
            videoEngagement: 'start',
            videoId: slug,
            videoLength: duration,
            videoPlaytime: 0,
        };

        Analytics.trackGoogleAnalytics(trackingData);

    }

    static trackVideoComplete({
        title,
        slug,
        duration,
        playtimeSeconds,
    }) {

        const trackingData = {
            event: 'video-engagement',
            videoName: title,
            videoEngagement: 'complete',
            videoId: slug,
            videoLength: duration,
            videoPlaytime: playtimeSeconds,
        };

        Analytics.trackGoogleAnalytics(trackingData);

    }

    static trackTranslationIdForVideoStart(translationId) {

        Analytics.triggerView(translationId);

    }

    static trackShareModalCloseButtonClick(slug) {

        const trackingData = {
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Share Close`,
            prop25: slug,
            eVar28: slug,
        };

        Analytics.track(trackingData);

    }

    static trackSortApplyButtonClick = (sort) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Sort`,
            prop14: `${PLAYBACK_PAGE} Module: Sort Apply: ${sort}`,
        });

    }

    static trackSortSelect = (sort) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Sort`,
            prop14: `${PLAYBACK_PAGE} Module: Sort Select: ${sort}`,
        });

    }

    static trackDeleteReelConfirmationModalCloseButtonClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Delete Reel Confirmation`,
            prop14: `${PLAYBACK_PAGE} Module: Delete Reel Confirmation: Close`,
        });

    }

    static trackDeleteReelConfirmationModalConfirmClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Delete Reel Confirmation`,
            prop14: `${PLAYBACK_PAGE} Module: Delete Reel Confirmation: Confirm`,
        });

    }

    static trackDeleteReelConfirmationModalCancelClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Delete Reel Confirmation`,
            prop14: `${PLAYBACK_PAGE} Module: Delete Reel Confirmation: Cancel`,
        });

    }

    static trackGameMatchupClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Game Matchup: Click`,
        });

    }

    static trackPlayerMatchupPlayerLinkClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Player Link Click`,
        });

    }

    static trackCarouselScroll = (carouselRank, carouselTitle, isScrollLeft) => {

        Analytics.track({
            prop12: 'Video Carousel',
            prop14: `Video Carousel: Scroll ${isScrollLeft ? 'Left' : 'Right'}`,
            eVar84: `${carouselRank}`,
            eVar103: carouselTitle,
        });

    }

    static trackCarouselOnItemClick = ({
        carouselRank,
        carouselTitle,
        videoRank,
        videoSlug,
    }) => {

        Analytics.track({
            prop12: 'Video Carousel',
            prop14: 'Video Carousel: Content Card Click',
            prop25: videoSlug,
            eVar84: `${carouselRank}:${videoRank}`,
            eVar103: carouselTitle,
        });

    }

    static trackCarouselTitleClick = (carouselRank, carouselTitle) => {

        Analytics.track({
            prop12: 'Video Carousel',
            prop14: 'Video Carousel: Title Click',
            eVar84: `${carouselRank}`,
            eVar103: carouselTitle,
        });

    }

    static trackResultsLinkClick = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module`,
            prop14: `${PLAYBACK_PAGE} Module: Back to Search Results Click`,
        });

    }

    static trackAppRuntimeError = () => {

        this.trackPageView(APP_RUNTIME_ERROR);

    }


    static trackFreeTextSearchFocus = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Free Text Search`,
            prop14: `${PLAYBACK_PAGE} Module: Free Text Search: Focus`,
        });

    }

    static trackFreeTextSearchClickEnterQuery = (searchQuery, click = true) => {

        const eventText = click ? 'Click': 'Enter';

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Free Text Search`,
            prop14: `${PLAYBACK_PAGE} Module: Free Text Search: ${eventText}: Query: ${searchQuery}`,
        });

    }

    static trackFreeTextSearchReset = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Free Text Search`,
            prop14: `${PLAYBACK_PAGE} Module: Free Text Search: Reset`,
        });

    }

    static trackFreeTextSearchNoResults = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Free Text Search`,
            prop14: `${PLAYBACK_PAGE} Module: Free Text Search: No Results`,
        });

    }

    static trackFreeTextSearchCancel = () => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module: Free Text Search`,
            prop14: `${PLAYBACK_PAGE} Module: Free Text Search: Cancel`,
        });

    }

    static trackFilterPanelCollapsibleClick = (filterName, expanded) => {

        Analytics.track({
            prop12: `${PLAYBACK_PAGE} Module Filters`,
            prop14: `${PLAYBACK_PAGE} Module: Filters: ${filterName}: ${ expanded ? 'See Less' : 'See More'}`,
        });

    }

};

export default Tracking;
